import Tracker from '@openreplay/tracker';
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ConfigProvider, theme } from "antd";
import { useEffect, useState } from "react";
import {
  Route,
  BrowserRouter as Router,
  Navigate,
  Routes as Switch
} from "react-router-dom";
import AdminContainer from "./components/layout";
import CancelPlanModal from './components/modals/cancel-plan-modal';
import TestExpiredModal from "./components/modals/test-expired-modal";
import UpgradePlanModal from "./components/modals/upgrade-plan-modal";
import DowngradePlanModal from './components/modals/downgrade-plan-modal';
import { GenerativeSessionProvider } from "./context/GenerativeSessionContext";
import OpenReplayContext from "./context/openreplay";
import ThemeContext from "./context/theme";
import AppEvents from './events';
import { PlatformProvider } from "./hooks/useShortcuts";
import DashboardNew from "./pages/Dashboard-New";
import Generative from "./pages/Generative";
import Plans from "./pages/Plans";
import TriggerTemplates from "./pages/Trigger-templates";
import ApiKeys from "./pages/apikeys";
import ChatBots from "./pages/chatbots";
import Companies from "./pages/companies";
import Dashboard from "./pages/dashboard";
import EditorIFrame from "./pages/editor-iframe";
import Evolution from "./pages/evolution";
import ForgotPassword from "./pages/forgot-password";
import InvalidApiKey from "./pages/invalid-apikey";
import Login from "./pages/login";
import Profile from "./pages/profile";
import Users from "./pages/users";
import MyCompany from "./pages/MyCompany";
import AcceptInvite from "./pages/accept-invite";
import ClientPlatforms from "@/pages/ClientPlatforms/index.jsx";
import AgentRequest from "./pages/AgentRequest";
import AgentRequestTracking from "./pages/AgentRequestTracking";
import AgentRequests from "./pages/AgentRequests";
import Onboarding from "./pages/onboarding";
import MCPOAuthCallback from "./pages/MCPOAuthCallback";

const devMode = import.meta.env.DEV
const googleId = import.meta.env.VITE_GOOGLE_ID

const tracker = new Tracker({
  projectKey: "es9AwBaEcdhhRrK6HjE6",
  __DISABLE_SECURE_MODE: devMode,
});

const publicAgentRequestPath = window.location.pathname.replace(/\/$/, '');
const isPublicAgentRequestPage = publicAgentRequestPath === '/lab/solicitar-agente' || publicAgentRequestPath.startsWith('/lab/atendimento/');
const isSensitiveOAuthCallback = publicAgentRequestPath === '/lab/oauth/callback';
if (!isPublicAgentRequestPage && !isSensitiveOAuthCallback) tracker.start();

function Routes() {
  const [actualTheme, setActualTheme] = useState(localStorage.getItem('theme'));
  const { defaultAlgorithm, darkAlgorithm } = theme;

  useEffect(() => {
    localStorage.setItem('theme', actualTheme)
    document.documentElement.dataset.theme = actualTheme === 'dark' ? 'dark' : 'light'
  }, [actualTheme])

  return (
    <OpenReplayContext.Provider value={tracker}>
      <ThemeContext.Provider value={[actualTheme, setActualTheme]}>
        <GoogleOAuthProvider clientId={googleId}>
          <ConfigProvider
            theme={{
              cssVar: true,
              algorithm: actualTheme === 'dark' ? darkAlgorithm : defaultAlgorithm,
              token: {
                hashed: true,
                colorPrimary: "#AD34FE",
                colorSuccess: "#34FEAD",
                colorWarning: "#FEAD34",
                colorError: "#FE3456",
                colorInfo: "#34ADFE",
                colorSider: "#1A0E24",
                colorBgContainer: actualTheme === 'dark' ? "#120a19" : "#fff",
                wireframe: false,
                borderRadius: 16,
              },
            }}
          >
            <PlatformProvider>
              <GenerativeSessionProvider>
                  <Router basename={"lab"}>
                    <Switch>
                      <Route path={'/login'} element={<Login />} />
                      <Route path={'/signup'} element={<Navigate to="/solicitar-agente" replace />} />
                      <Route path={'/forgot/password'} element={<ForgotPassword />} />
                      <Route path={'/iframe/invalid'} element={<InvalidApiKey />} />
                      <Route path={'/iframe/editor/:snapshot/*'} element={<EditorIFrame />} />
                      <Route path={'/invite/:token'} element={<AcceptInvite />} />
                      <Route path={'/solicitar-agente'} element={<AgentRequest />} />
                      <Route path={'/atendimento/:trackingId'} element={<AgentRequestTracking />} />
                      <Route path={'/oauth/callback'} element={<MCPOAuthCallback />} />
                      <Route path={'*'} element={
                        <AdminContainer>
                          <Switch>
                            <Route path={'/chatbots/*'} element={<ChatBots />} />
                            <Route path={'/assistants/*'} element={<Generative />} />
                            <Route path={'/client-platforms/*'} element={<ClientPlatforms />} />
                            <Route path={'/companies/*'} element={<Companies />} />
                            <Route path={'/agent-requests/*'} element={<AgentRequests />} />
                            <Route path={'/my-company/*'} element={<MyCompany />} />
                            <Route path={'/templates/*'} element={<TriggerTemplates />} />
                            <Route path={'/users/*'} element={<Users />} />
                            <Route path={'/profile/:id'} element={<Profile />} />
                            <Route path={'/api-keys/*'} element={<ApiKeys />} />
                            <Route path={'/plans/*'} element={<Plans />} />
                            <Route path={'/evolution/*'} element={<Evolution />} />
                            <Route path={'/onboarding'} element={<Onboarding />} />
                            <Route path={'/'} element={<DashboardNew />} />
                            <Route path={'/old'} element={<Dashboard />} />
                          </Switch>
                        </AdminContainer>
                      }></Route>
                    </Switch>
                  </Router>

                  <TestExpiredModal />
                  <CancelPlanModal />
                  <UpgradePlanModal />
                  <DowngradePlanModal />

                  <AppEvents />

                </GenerativeSessionProvider>
            </PlatformProvider>
          </ConfigProvider>
        </GoogleOAuthProvider>
      </ThemeContext.Provider>
    </OpenReplayContext.Provider>
  )
}

export default Routes;
